import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Stack,
  Typography,
  Box,
  Button,
  TableRow,
  TableCell,
} from "@mui/material";
import {
  NoteAdd as NoteAddIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { Patient as PatientModal } from "../../../models/Patient";
import { Paths as PathsConstant } from "../../../constants/Route";
import { RowsPerPage as RowsPerPageConstant } from "../../../constants/Table";
import { FormatDisplayHn as FormatDisplayHnUtil } from "../../../utils/InputValidation";
import { DateFormatter as DateFormatterUtil } from "../../../utils/FormatDate";
import { useStores } from "../../../stores";
import {
  GetSearchPostoperative as GetSearchPostoperativeService,
  GetCountPostoperativeBySearch as GetCountPostoperativeBySearchService,
  DeletePostoperative as DeletePostoperativeService,
} from "../../../services/colorectals/Postoperative";
import AuthenticatedLayout from "../../../layouts/Authenticated";
import SearchAndFilterByDateAndKeyword from "../../../components/filters/SearchAndFilterByDateAndKeyword";
import SimpleTable from "../../../components/tables/Simple";
import ModalDelete from "../../../components/modals/Delete";
import ModalFail from "../../../components/modals/Fail";
import LoadingWholeScreen from "../../../components/loading/WholeScreen";
import ModalPatientInformation from "../../../components/patients/ModalInformation";
import Sorting from "../../../components/filters/Sorting";

interface Props {}

const List = (props: Props) => {
  const navigate = useNavigate();
  const {
    HistoryStore,
    PatientStore,
    PreoperativeStore,
    PostoperativeStore,
    OperativeNoteStore,
  } = useStores();
  const columns = [
    {
      name: "Records",
      align: "center",
      className: "min-w-160",
    },
    {
      name: "Patient (HN)",
      align: "center",
      className: "min-w-250 txt-link",
    },
    {
      name: "Date",
      align: "center",
      className: "min-w-100",
    },
    {
      name: "Action",
      align: "center",
      className: "",
    },
  ];
  const [searchQuery, setSearchQuery] = useState({
    keyword: "",
    startDate: "",
    endDate: "",
  });
  const [postoperativeForms, setPostoperativeForms] = useState([]);
  const [totalPostoperativeForms, setTotalPostoperativeForms] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCount, setIsLoadingCount] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowPerPage, setRowPerPage] = useState(RowsPerPageConstant);
  const [sorting, setSorting] = useState<any>({ created_at: -1, reference_no: -1 });
  const [postoperativeFormSelected, setPostoperativeFormSelected] =
    useState<any>(null);
  const [isModalDeleteOpen, setIsModalDeleteOpen] = useState(false);
  const [isShowFailModal, setIsShowFailModal] = useState(false);
  const [isLoadingWholeScreen, setIsLoadingWholeScreen] = useState(false);
  const [isModalPatientInformationOpen, setIsModalPatientInformationOpen] =
    useState(false);
  let onGetSearchPostoperativeForms = () => {};
  let onGetCountPostoperativeForms = () => {};

  useEffect(() => {
    PatientStore.onClearStore();
    HistoryStore.onClearStore();
    PreoperativeStore.onClearStore();
    PostoperativeStore.onClearStore();
    OperativeNoteStore.onClearStore();
  }, [
    PatientStore,
    HistoryStore,
    PreoperativeStore,
    PostoperativeStore,
    OperativeNoteStore,
  ]);
  useEffect(() => {
    onGetSearchPostoperativeForms();
  }, [currentPage, rowPerPage, searchQuery, sorting]);
  useEffect(() => {
    onGetCountPostoperativeForms();
  }, [searchQuery]);

  onGetSearchPostoperativeForms = async () => {
    setIsLoading(true);
    setPostoperativeForms([]);
    const params = {
      ...(searchQuery.keyword && { keyword: searchQuery.keyword }),
      ...(searchQuery.startDate && { startDate: searchQuery.startDate }),
      ...(searchQuery.endDate && { endDate: searchQuery.endDate }),
      ...(sorting && { sort: JSON.stringify({ ...sorting }) }),
      skip: currentPage * rowPerPage,
      limit: rowPerPage,
    };
    const response = await GetSearchPostoperativeService({ ...params });
    response.success && setPostoperativeForms(response.data);
    setIsLoading(false);
  };
  onGetCountPostoperativeForms = async () => {
    setIsLoadingCount(true);
    setTotalPostoperativeForms(0);
    const params = {
      ...(searchQuery.keyword && { keyword: searchQuery.keyword }),
      ...(searchQuery.startDate && { startDate: searchQuery.startDate }),
      ...(searchQuery.endDate && { endDate: searchQuery.endDate }),
    };
    const response = await GetCountPostoperativeBySearchService({ ...params });
    response.success && setTotalPostoperativeForms(response.data);
    setIsLoadingCount(false);
  };
  const onResetSearchPostoperative = () => {
    setSearchQuery({
      keyword: "",
      startDate: "",
      endDate: "",
    });
  };
  const onSubmitSearch = (query: any) => {
    setSearchQuery({ ...query });
    handleChangePage(0, rowPerPage);
  };
  const handleChangePage = (page: number, rowPerPage: number) => {
    setCurrentPage(page);
    setRowPerPage(rowPerPage);
  };
  const handleSorting = (value: any) => {
    setSorting(value);
  };
  const onNavigateToPostoperativePreview = (postoperative: any) => {
    navigate(
      `${PathsConstant.colorectalSurgeryPostOperativePreview}/${postoperative.patient?.id}/${postoperative.id}`
    );
  };
  const onNavigateToColorectalPostoperativeEdit = (postoperative: any) => {
    navigate(
      `${PathsConstant.colorectalSurgeryPostOperativeEdit}/${postoperative.patient?.id}/${postoperative?.id}`
    );
  };
  const handleOpenModalDelete = (postoperativeForm: any) => {
    setPostoperativeFormSelected({ ...postoperativeForm });
    setIsModalDeleteOpen(true);
  };
  const handleCloseModalDelete = () => {
    setPostoperativeFormSelected(null);
    setIsModalDeleteOpen(false);
  };
  const handleDeletePostoperativeForm = async () => {
    setIsModalDeleteOpen(false);
    setIsLoadingWholeScreen(true);
    const response = await DeletePostoperativeService(
      postoperativeFormSelected?.id
    );
    setIsLoadingWholeScreen(false);
    if (response.success) {
      handleCloseModalDelete();
      onGetSearchPostoperativeForms();
      onGetCountPostoperativeForms();
    } else {
      setIsShowFailModal(true);
    }
  };
  const handleOpenModalPatientInformation = (patient: PatientModal) => {
    PatientStore.onSetIsReadOnly(true);
    PatientStore.GetPatientById(patient?.id || "");
    setIsModalPatientInformationOpen(true);
  };
  const handleCloseModalPatientInformation = () => {
    setIsModalPatientInformationOpen(false);
    PatientStore.onClearStore();
  };

  return (
    <AuthenticatedLayout>
      <>
        {isLoadingWholeScreen && (
          <LoadingWholeScreen isShowDescription={true} />
        )}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "center" }}
        >
          <Typography variant="h5">List of Postoperative</Typography>
          <Box
            marginLeft={{ sm: "auto" }}
            marginTop={{ xs: 2, sm: 0 }}
            width={{ xs: "100%", sm: "auto" }}
          >
            <Button
              variant="contained"
              onClick={() =>
                navigate(
                  `${PathsConstant.colorectalSurgeryPostOperativeSearchPatient}?formType=postoperative`
                )
              }
            >
              <NoteAddIcon />
              Create Form
            </Button>
          </Box>
        </Stack>
        <Box marginY={4}>
          <SearchAndFilterByDateAndKeyword
            placeholderKeyword="Search by first name, last name, hn, id card"
            onReset={onResetSearchPostoperative}
            onSearch={(body: any) => onSubmitSearch(body)}
          />
        </Box>
        <Stack marginBottom={2} alignItems="flex-end">
          <Sorting
            defaultSortingValue="Created (Newest)"
            onSorting={(value: any) => handleSorting(value)}
          />
        </Stack>
        <SimpleTable
          columns={columns}
          totalRows={totalPostoperativeForms}
          page={currentPage}
          isLoading={isLoading || isLoadingCount}
          onChangePage={(page: number, rowPerPage: number) => {
            handleChangePage(page, rowPerPage);
          }}
        >
          <>
            {postoperativeForms?.map(
              (postoperativeForm: any, postoperativeFormIndex: number) => {
                return (
                  <TableRow hover tabIndex={-1} key={postoperativeFormIndex}>
                    <TableCell align="center" className={columns[0]?.className}>
                      <Typography variant="subtitle1">
                        {(postoperativeForm.referenceNo &&
                          `Postoperative / ${postoperativeForm.referenceNo}`) ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={columns[1]?.className}>
                      <Typography
                        variant="subtitle1"
                        onClick={() => {
                          handleOpenModalPatientInformation(
                            postoperativeForm.patient
                          );
                        }}
                      >
                        {postoperativeForm.patient?.firstName}{" "}
                        {postoperativeForm.patient?.lastName}{" "}
                        {(postoperativeForm.patient?.hn &&
                          `(${FormatDisplayHnUtil(
                            postoperativeForm.patient?.hn
                          )})`) ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className={columns[2]?.className}>
                      <Typography variant="subtitle1">
                        {(postoperativeForm.createdAt &&
                          DateFormatterUtil(postoperativeForm.createdAt)) ||
                          "-"}
                      </Typography>
                    </TableCell>
                    <TableCell align="center" className="cell-action">
                      <Grid
                        container
                        alignItems="center"
                        justifyContent="center"
                        spacing={2}
                      >
                        <Grid item>
                          <Box
                            className="action-button selected-icon"
                            onClick={() =>
                              onNavigateToPostoperativePreview(
                                postoperativeForm
                              )
                            }
                          >
                            <SearchIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box
                            className="action-button edit-icon"
                            onClick={() =>
                              onNavigateToColorectalPostoperativeEdit(
                                postoperativeForm
                              )
                            }
                          >
                            <EditIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Grid>
                        <Grid item>
                          <Box
                            className="action-button delete-icon"
                            onClick={() =>
                              handleOpenModalDelete(postoperativeForm)
                            }
                          >
                            <DeleteIcon sx={{ fontSize: 20 }} />
                          </Box>
                        </Grid>
                      </Grid>
                    </TableCell>
                  </TableRow>
                );
              }
            )}
          </>
        </SimpleTable>
        <ModalDelete
          isOpen={isModalDeleteOpen}
          title="Delete postoperative"
          description="Are you sure you want to delete postoperative"
          information={`${
            postoperativeFormSelected?.patient?.firstName || ""
          } ${postoperativeFormSelected?.patient?.lastName || ""} / ${
            postoperativeFormSelected?.referenceNo || ""
          }`}
          onClose={handleCloseModalDelete}
          onDelete={handleDeletePostoperativeForm}
        />
        <ModalFail
          isOpen={isShowFailModal}
          title="Delete postoperative"
          description="Failed to delete a postoperative. Please try again."
          onClose={() => {
            setIsShowFailModal(false);
          }}
        />
        <ModalPatientInformation
          isOpen={isModalPatientInformationOpen}
          onClose={handleCloseModalPatientInformation}
        />
      </>
    </AuthenticatedLayout>
  );
};

export default List;
